// AdminAiCosts: what each tailoring run really cost, from the server's ledger
// (public.ai_usage_log). Every Claude call is logged when it starts and when it
// returns, so failed attempts, retries and calls that never reported back show.
import { Fragment, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ChevronDown, ChevronRight, DollarSign, Loader2, RefreshCw } from "lucide-react";

type Row = {
  id: string; created_at: string; finished_at: string | null; fn: string; stage: string | null; label: string | null;
  run_id: string | null; run_label: string | null; model: string | null; status: string; http_status: number | null;
  error: string | null; input_tokens: number | null; output_tokens: number | null; cache_read_tokens: number | null;
  cache_write_tokens: number | null; est_input_tokens: number | null; cost_usd: number | null; ms: number | null;
};

const DAYS = 30;
const STALE_MS = 10 * 60 * 1000; // no reply after this long: the call never reported back
const money = (n: number) => `$${n.toFixed(n !== 0 && n < 1 ? 4 : 2)}`;
const when = (iso: string) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const secs = (ms: number) => (ms >= 60000 ? `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s` : `${Math.round(ms / 1000)}s`);

const isLost = (r: Row) => r.status === "started" && Date.now() - new Date(r.created_at).getTime() > STALE_MS;
const isFailed = (r: Row) => r.status === "error" || r.status === "aborted";
const tokens = (r: Row) => (r.input_tokens ?? 0) + (r.output_tokens ?? 0) + (r.cache_read_tokens ?? 0) + (r.cache_write_tokens ?? 0);

function summarize(rows: Row[]) {
  return {
    cost: rows.reduce((s, r) => s + Number(r.cost_usd ?? 0), 0),
    tokens: rows.reduce((s, r) => s + tokens(r), 0),
    calls: rows.length,
    failed: rows.filter(isFailed).length,
    lost: rows.filter(isLost).length,
    unpriced: rows.filter((r) => r.status === "ok" && r.cost_usd === null).length,
  };
}

export default function AdminAiCosts() {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const since = new Date(Date.now() - DAYS * 86400000).toISOString();
      const all: Row[] = [];
      for (let from = 0; ; from += 1000) {
        const { data, error: e } = await (supabase as any).from("ai_usage_log").select("*")
          .gte("created_at", since).order("created_at", { ascending: false }).range(from, from + 999);
        if (e) throw e;
        all.push(...(data as Row[]));
        if ((data as Row[]).length < 1000) break;
      }
      setRows(all);
    } catch (e: any) {
      setError(e?.message ?? "Could not load the costs.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  const view = useMemo(() => {
    if (!rows) return null;
    const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
    const week = Date.now() - 7 * 86400000;
    const runs = new Map<string, Row[]>();
    const other = new Map<string, Row[]>();
    for (const r of rows) {
      const map = r.run_id ? runs : other;
      const key = r.run_id ?? r.fn;
      map.set(key, [...(map.get(key) ?? []), r]);
    }
    return {
      today: summarize(rows.filter((r) => new Date(r.created_at) >= startOfDay)),
      week: summarize(rows.filter((r) => new Date(r.created_at).getTime() >= week)),
      month: summarize(rows),
      runs: [...runs.entries()].map(([id, rs]) => ({ id, rows: [...rs].reverse(), ...summarize(rs) })),
      other: [...other.entries()].map(([fn, rs]) => ({ fn, ...summarize(rs) })).sort((a, b) => b.cost - a.cost),
    };
  }, [rows]);

  return (
    <div className="p-6 space-y-5 max-w-6xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[#0B1C3F]">
          <DollarSign className="w-5 h-5" />
          <h1 className="text-lg font-bold">AI Costs</h1>
          <span className="text-[13px] text-slate-500">Claude spend from the server, last {DAYS} days</span>
        </div>
        <button onClick={() => void load()} disabled={loading} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-800">{error}</div>}
      {!view && !error && <div className="text-[13px] text-slate-500">Loading...</div>}

      {view && (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            {([["Today", view.today], ["Last 7 days", view.week], [`Last ${DAYS} days`, view.month]] as const).map(([name, s]) => (
              <div key={name} className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-[13px] text-slate-600">
                <div className="font-semibold text-[#0B1C3F]">{name}: {money(s.cost)}</div>
                <div>{s.calls} calls, {s.tokens.toLocaleString()} tokens</div>
                {(s.failed > 0 || s.lost > 0) && <div className="text-amber-700">{s.failed} failed, {s.lost} never reported back</div>}
                {s.unpriced > 0 && <div className="text-amber-700">{s.unpriced} calls on a model with no price set</div>}
              </div>
            ))}
          </div>

          <p className="text-[12.5px] text-slate-500">
            Dollars are worked out from Anthropic's list prices and the tokens each reply reported. A call that never
            reported back has no token count, so it can cost money that is not in these totals. Compare a day's total with the Anthropic bill to confirm.
          </p>

          <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="px-3 py-2 font-medium">Run</th>
                  <th className="px-3 py-2 font-medium">Started</th>
                  <th className="px-3 py-2 font-medium text-right">Calls</th>
                  <th className="px-3 py-2 font-medium text-right">Tokens</th>
                  <th className="px-3 py-2 font-medium text-right">Cost</th>
                  <th className="px-3 py-2 font-medium">Problems</th>
                </tr>
              </thead>
              <tbody>
                {view.runs.length === 0 && <tr><td colSpan={6} className="px-3 py-3 text-slate-500">No runs recorded yet.</td></tr>}
                {view.runs.map((run) => (
                  <Fragment key={run.id}>
                    <tr className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setOpen(open === run.id ? null : run.id)}>
                      <td className="px-3 py-2 font-medium text-[#0B1C3F]">
                        <span className="inline-flex items-center gap-1">
                          {open === run.id ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                          {run.rows[0]?.run_label || "Tailoring run"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-600">{when(run.rows[0].created_at)}</td>
                      <td className="px-3 py-2 text-right">{run.calls}</td>
                      <td className="px-3 py-2 text-right">{run.tokens.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-semibold">{money(run.cost)}</td>
                      <td className="px-3 py-2 text-amber-700">{[run.failed ? `${run.failed} failed` : "", run.lost ? `${run.lost} never reported back` : ""].filter(Boolean).join(", ")}</td>
                    </tr>
                    {open === run.id && (
                      <tr className="border-b border-slate-100 bg-slate-50/60">
                        <td colSpan={6} className="px-3 py-2">
                          <table className="w-full text-[12.5px]">
                            <thead>
                              <tr className="text-left text-slate-500">
                                <th className="py-1 font-medium">Step</th><th className="py-1 font-medium">Model</th><th className="py-1 font-medium">Status</th>
                                <th className="py-1 font-medium text-right">In</th><th className="py-1 font-medium text-right">Cached</th><th className="py-1 font-medium text-right">Out</th>
                                <th className="py-1 font-medium text-right">Time</th><th className="py-1 font-medium text-right">Cost</th>
                              </tr>
                            </thead>
                            <tbody>
                              {run.rows.map((r) => (
                                <tr key={r.id} title={r.error ?? undefined}>
                                  <td className="py-0.5">{r.label || r.stage || r.fn}</td>
                                  <td className="py-0.5 text-slate-500">{r.model}</td>
                                  <td className={`py-0.5 ${r.status === "ok" ? "text-slate-500" : "text-amber-700"}`}>{isLost(r) ? "never reported back" : r.status}{r.http_status && r.status !== "ok" ? ` ${r.http_status}` : ""}</td>
                                  <td className="py-0.5 text-right">{(r.input_tokens ?? 0).toLocaleString()}</td>
                                  <td className="py-0.5 text-right">{((r.cache_read_tokens ?? 0) + (r.cache_write_tokens ?? 0)).toLocaleString()}</td>
                                  <td className="py-0.5 text-right">{(r.output_tokens ?? 0).toLocaleString()}</td>
                                  <td className="py-0.5 text-right">{r.ms ? secs(r.ms) : ""}</td>
                                  <td className="py-0.5 text-right">{r.cost_usd === null ? "" : money(Number(r.cost_usd))}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {view.other.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
              <div className="text-[13px] font-semibold text-[#0B1C3F] mb-1">Spend outside a tailoring run</div>
              {view.other.map((o) => (
                <div key={o.fn} className="flex justify-between text-[13px] text-slate-600 py-0.5">
                  <span>{o.fn}: {o.calls} calls{o.failed ? `, ${o.failed} failed` : ""}{o.lost ? `, ${o.lost} never reported back` : ""}</span>
                  <span className="font-medium">{money(o.cost)}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
